import _ from 'lodash';
import React, { PropTypes } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import actions from '../../actions';
import {
	Button,
	Pane,
	Spinner,
	Alert,
	CorporationFound,
	CorporationFeedLink,
} from '../../components';

// Import the CSS
import '../../../scss/containers/feeds/_link-pane.scss';

const initialState = {
	requestId: '',
	showDropdown: {},
	selectedCorporations: {},
	updatedCorporations: {},
	knownCorporations: [],
	newCorporations: [],
	linkWarning: {},
};

export class LinkCorporationPane extends Pane {

	constructor (props) {
		super(props);

		this.state = initialState;
	}

	componentWillReceiveProps (nextProps) {
		// Parse the feed XML when the pane opens (i.e. goes from invisible to visible)
		if (!this.props.visible && nextProps.visible && nextProps.feed.source) {

			const requestId = Math.random().toString().substr(2);
			this.setState({ requestId });

			this.props.actions.fetchFeedXml(requestId, nextProps.feed);

			this.props.actions.fetchCorporationFeedsList(nextProps.feed.id);
		}

		// The following constants indicate whether the arrival of the next props completes the data necessary
		// to determine which items in the list of extracted corporations have hidden action buttons.
		// Data fetching is complete if both the corporation-feeds list and extracted corporations are available.
		//
		// The constants check for two possible cases of data completion (one needs to be true to assume completion):
		// - isDataFetchCompletedWithCorpFeeds: the extracted corporation data has already been fetched, and the next
		// props contain the corporation-feeds list
		// - isDataFetchCompletedWithExtractedCorps: the corporation-feeds list has already been fetched, and the next
		// props contain the extracted corporation data
		const isDataFetchCompletedWithCorpFeeds = (
			!nextProps.corporationFeeds.isFetching &&
			!_.isEmpty(nextProps.feeds.extractedCorporations) &&
			!_.isEqual(this.props.corporationFeeds.list, nextProps.corporationFeeds.list)
		);
		const isDataFetchCompletedWithExtractedCorps = (
			!nextProps.corporationFeeds.isFetching &&
			!_.isEmpty(nextProps.corporationFeeds.list) &&
			!_.isEqual(this.props.feeds.extractedCorporations, nextProps.feeds.extractedCorporations)
		);

		// Once both extracted corporations and corporation-feeds have completed fetching,
		// determine which extracted corporations are known and thus are not shown in the new corp list
		if (isDataFetchCompletedWithCorpFeeds || isDataFetchCompletedWithExtractedCorps) {

			const knownCorporations = [];
			const linkedCorporations = nextProps.corporationFeeds.list.corporations;
			const extractedCorpNumbers = nextProps.feeds.extractedCorporations.map(corp => corp.number);

			if (_.isEmpty(linkedCorporations)) {

				// For a feed with no linked corporations, all corporations found in the feed are new
				this.setState({ newCorporations: nextProps.feeds.extractedCorporations });

			} else {

				// Collect the numbers of extracted corporations that match with that of previously linked corporations
				// for use in hiding known corporations
				linkedCorporations.forEach(linkedCorp => {
					if (extractedCorpNumbers.indexOf(linkedCorp.number) !== -1) {
						knownCorporations.push(linkedCorp.number);
					}
				});

				// Set both the new and known corporations
				this.setState({
					knownCorporations,
					newCorporations: nextProps.feeds.extractedCorporations.filter(corp => {
						return !_.find(linkedCorporations, { number: corp.number });
					})
				});

			}
		}

	}

	close () {
		this.props.actions.clearCorporationState();

		// Abort ongoing fetch/parse requests
		const isProcessingFeed = (
			this.props.feeds.isFetchingXml ||
			this.props.feeds.isExtractingXml ||
			this.props.feeds.parsingXmlNames.length
		);

		// Cancel feed loading if necessary and reset relevant store states
		if (isProcessingFeed) {
			this.props.actions.cancelFeedLoad(this.state.requestId);
		} else {
			this.props.actions.clearFeedXmlState();
		}

		// Reset local state
		this.setState(initialState);

		super.close();
	}

	handleClickCreateNew (corporation, key) {
		this.props.actions.createCorporation({
			feedId: this.props.feed.id,
			number: corporation.number,
			name: corporation.name,
			state: corporation.state,
			email: corporation.email,
			emailCorporateOnly: corporation.emailCorporateOnly,
			isFeedEnabled: this.props.feed.isEnabled,
		}, key);
	}

	handleClickFindMatch (key) {
		this.setState({
			showDropdown: {
				...this.state.showDropdown,
				[key]: true
			}
		});
	}

	handleClickUpdate (corporation, key) {
		if (this.state.newCorporations.length === 1) {

			this.setState({
				updatedCorporations: {
					...this.state.updatedCorporations,
					[key]: { ...corporation, with: `new-corp-0` },
				},
			});

			this.props.actions.editCorporation({
				...this.state.newCorporations[0],
				id: corporation.id,
			}, key);

		} else if (this.state.newCorporations.length > 1) {
			this.setState({
				showDropdown: {
					...this.state.showDropdown,
					[key]: true
				}
			});
		}
	}

	handleClickSearchResult (option, corporation, key) {

		// Default state for clicking search result should hide auto-complete
		const newState = {
			showDropdown: {
				...this.state.showDropdown,
				[key]: false,
			},
		};

		// Find corporation object from search results by id
		const selectedCorporation = option.value.corporation;

		// The selected corporation belongs to a another feed
		// In the case where the selected and extracted corporation match numbers, link the corporation
		// Otherwise, display a message to create a new corporation for when the numbers do not match
		if (selectedCorporation.number === corporation.number) {

			this.props.actions.linkCorporation(selectedCorporation.id, this.props.feed.id, key);
			newState.selectedCorporations = {
				...this.state.selectedCorporations,
				[key]: selectedCorporation,
			};

		} else {

			newState.linkWarning = {
				...this.state.linkWarning,
				[key]: {
					key: `warning-message-${Math.random()}`,
					text: 'You must create a new corporation.'
				},
			};

		}

		this.setState(newState);
	}

	handleClickLinkCorporation (key, link = true) {
		if (link) {
			this.props.actions.linkCorporation(this.state.selectedCorporations[key].id, this.props.feed.id, key);
		} else {
			this.props.actions.unlinkCorporation(this.state.selectedCorporations[key].id, this.props.feed.id, key);
		}
	}

	handleChangeUpdate (option, corporation, key) {

		this.setState({
			showDropdown: {
				...this.state.showDropdown,
				[key]: false,
			},
			updatedCorporations: {
				...this.state.updatedCorporations,
				[key]: { ...corporation, with: option.key },
			},
		});

		this.props.actions.editCorporation({
			...option.corporation,
			id: corporation.id,
		}, key);

	}

	formatOptions (corporations, key) {
		if (!_.isEmpty(corporations)) {
			return corporations.map((corporation, i) => ({
				value: { corporation, key: `${key}-${i}` },
				label: (
					<div className="corporation-details option">
						<div> { corporation.name } </div>
						<div> { corporation.number } </div>
					</div>
				),
			}));
		}
	}

	renderFooter () {

		const isCancelable = (
			this.props.feeds.isFetchingXml ||
			this.props.feeds.isExtractingXml ||
			this.props.feeds.parsingXmlNames.length
		);

		return (
			<div className="pane-footer">
				<Button type="submit" size="small" className="pull-right" onClick={ () => this.close() }>
					{ isCancelable ? 'Cancel' : 'Done' }
				</Button>
			</div>
		);
	}

	renderContent () {

		const isDoneProcessingFeed = (
			!this.props.error &&
			!this.props.feeds.isFetchingXml &&
			!this.props.feeds.isExtractingXml &&
			!this.props.feeds.parsingXmlNames.length
		);

		return (
			<div className="corporation-linking-pane-container">

				<div className="corporation-linking-pane-new-corporations">

					{ this.props.feeds.xmlParseWarnings.map(warning => (
						<Alert key={ warning.id } style="warning" canDismiss onClick={ () => this.props.actions.clearParseFeedXmlWarning(warning.id) } >
							{ warning.message }
						</Alert>
					)) }

					<Choose>

						{/* Display loading spinner when extracting xml */}
						<When condition={ this.props.feeds.isExtractingXml }>
							<div className="corporation-linking-pane-list">
								<div className="corporation-linking-pane-row">
									<div className="corporation-details pane-loading">
										<Spinner style="inline" />
										<span> Extracting from zip file... </span>
									</div>
								</div>
							</div>
						</When>

						{/* Display loading spinner when fetching xml */}
						<When condition={ this.props.feeds.isFetchingXml }>
							<div className="corporation-linking-pane-list">
								<div className="corporation-linking-pane-row">
									<div className="corporation-details pane-loading">
										<Spinner style="inline" />
										<span> Fetching XML Feed... </span>
									</div>
								</div>
							</div>
						</When>

					</Choose>

					<Choose>

						<When condition={ isDoneProcessingFeed && !this.state.newCorporations.length }>
							<Alert style="info">No new corporations found in this feed.</Alert>
						</When>

						{/* Render extracted corporations list upon completing fetching/extracting the xml */}
						<When condition={ !this.props.error }>
							<div className="corporation-linking-pane-list">
								{ this.state.newCorporations.map((corporation, i) => {

									// Synchronize both new corporations found & existing corporation links to show
									// the update status for each new corporation
									const key = `new-corp-${i}`;
									let status = this.props.corporations.statusKeys[key];
									const isKnownCorporation = this.state.knownCorporations.indexOf(corporation.number) > -1;

									if (!isDoneProcessingFeed || isKnownCorporation) {
										status = 'hidden';
									} else {
										_.each(this.state.updatedCorporations, (updatedCorp, linkIndex) => {
											if (updatedCorp.with === key) {
												status = this.props.corporations.statusKeys[linkIndex];
											}
										});
									}

									return (
										<CorporationFound
											key={ key }
											status={ status }
											corporation={ corporation }
											isSearching={ this.props.corporations.searchStatusKeys[key] }
											isFetchingLinks={ this.props.corporationFeeds.isFetching }
											onCreateNew={ () => this.handleClickCreateNew(corporation, key) }
											onFindMatch={ () => this.handleClickFindMatch(key) }
											onChange={ option => this.handleClickSearchResult(option, corporation, key) }
											onInput={ value => this.props.actions.fetchAutoCompleteCorporationList(`%${value}%`, `%${corporation.number}%`, key) }
											onLink={ link => this.handleClickLinkCorporation(key, link) }
											selected={ this.state.selectedCorporations[key] }
											showAutoComplete={ this.state.showDropdown[key] }
											options={ this.formatOptions(this.props.corporations.searchResults[key], 'search-result') }
											searchError={ this.props.corporations.searchError[key] }
											createError={ this.props.corporations.createError[key] }
											linkError={ this.props.corporations.linkError[key] }
											warningMessage={ this.state.linkWarning[key] } />
									);
								} ) }

								<If condition={ this.props.feeds.parsingXmlNames.length }>
									{ this.props.feeds.parsingXmlNames.map((fileName, i) => (
										<div key={`parsing-${fileName}-${i}`} className="corporation-linking-pane-row">
											<div className="corporation-details pane-loading">
												<Spinner style="inline" />
												<span> { fileName ? `Parsing "${fileName}"...` : 'Parsing XML Feed...' } </span>
											</div>
										</div>
									)) }
								</If>

							</div>

						</When>

					</Choose>

				</div>

				<h5 className="corporation-linking-divider"></h5>
				<h5> Existing Corporation Links </h5>

				<div className="corporation-linking-pane-existing-links">
					<Choose>
						<When condition={ this.props.corporationFeeds.isFetching }>
							<div className="corporation-linking-pane-list">
								<div className="corporation-linking-pane-row">
									<div className="corporation-details pane-loading">
										<Spinner style="inline" />
										<span> Fetching linked corporations... </span>
									</div>
								</div>
							</div>
						</When>
						<When condition={
							this.props.corporationFeeds.list.corporations &&
							this.props.corporationFeeds.list.corporations.length
						}>
							<div className="corporation-linking-pane-list">
								{ // Display the fetched corporation-feed links
									this.props.corporationFeeds.list.corporations.map((corporation, i) => {
										const key = `linked-corp-${i}`;
										const showUpdateButton = isDoneProcessingFeed && !!this.state.newCorporations.length;
										return (
											<CorporationFeedLink
												key={ key }
												corporation={ corporation }
												selected={ this.state.updatedCorporations[key] }
												error={ this.props.corporations.updateError[key] }
												status={ this.props.corporations.statusKeys[key] }
												showDropdown={ this.state.showDropdown[key] }
												showUpdateButton={ showUpdateButton }
												onChange={ option => this.handleChangeUpdate(option, corporation, key) }
												options={ this.formatOptions(this.state.newCorporations, 'new-corp') }
												onUpdate={ () => this.handleClickUpdate(corporation, key) } />
										);
									})
								}
							</div>
						</When>
						<Otherwise>
							<Alert style="info">No corporations are currently linked to this feed.</Alert>
						</Otherwise>
					</Choose>
				</div>

			</div>
		);

	}
}

LinkCorporationPane.propTypes = {
	title: PropTypes.string,
	visible: PropTypes.bool,
	feed: PropTypes.object,
	onClose: PropTypes.func,
	onSubmit: PropTypes.func,
};

export default connect(
	state => ({
		feeds: {
			parsingXmlNames: state.feeds.parsingXmlNames,
			isFetchingXml: state.feeds.isFetchingXml,
			isExtractingXml: state.feeds.isExtractingXml,
			extractedCorporations: state.feeds.extractedCorporations,
			xmlParseWarnings: state.feeds.xmlParseWarnings,
		},
		corporations: {
			statusKeys: state.corporations.statusKeys,
			searchResults: state.corporations.searchResults,
			linkStatusKeys: state.corporations.linkStatusKeys,
			searchStatusKeys: state.corporations.searchStatusKeys,
			createError: state.corporations.error.create || {},
			updateError: state.corporations.error.update || {},
			searchError: state.corporations.error.search || {},
			linkError: state.corporations.error.link || {},
		},
		corporationFeeds: {
			list: state.corporationFeeds.list,
			error: state.corporationFeeds.error,
			isFetching: state.corporationFeeds.isFetching,
		},
		error: state.feeds.error,
	}),
	dispatch => ({
		actions: {
			...bindActionCreators(actions.feeds, dispatch),
			...bindActionCreators(actions.corporationFeeds, dispatch),
			...bindActionCreators(_.pick(actions.corporations, [
				'createCorporation',
				'editCorporation',
				'fetchAutoCompleteCorporationList',
				'linkCorporation',
				'unlinkCorporation',
				'clearCorporationState',
			]), dispatch),
		},
	})
)(LinkCorporationPane);



// WEBPACK FOOTER //
// ./src/assets/js/containers/feeds/LinkCorporationPane.js